import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { IPreferencesDTO, IUserInfoDTO } from 'src/api/dtos';
import AuthRepository from 'src/api/repositories/common/v1/auth.repository';
import { IError } from 'src/api/types';
import { getRoute } from 'src/routes';
import { LocalStorageEnums } from 'src/shared/enums';
import { useTranslate } from 'src/shared/providers';
import { getLocalStorageValue, redirectToLogin } from 'src/shared/utils';
import { fetchCultures, fetchLocalizations } from 'src/store/localizations/localizations.actions';
import {
  fetchDefaultPreferences,
  fetchPreferences,
  fetchSystemSettings,
  fetchUserInfo,
  logout,
  setAutoAuthenticated,
} from 'src/store/user/user.actions';

import { useAppDispatch } from './useAppDispatch';
import { useGetLegacyUrl } from './useGetLegacyUrl';
import { useLocalStorage } from './useLocalStorage';
import { useThunkDispatch } from './useThunkDispatch';

const UPDATE_PASSWORD = 'update-password';

export const useAuth = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const dispatchThunk = useThunkDispatch();
  const { getLegacyUrlAsync } = useGetLegacyUrl();
  const { setValue: setIsLegacyToBoltSso } = useLocalStorage<boolean>(LocalStorageEnums.LOGGED_IN_WITH_SSO, false);

  // BFF Pattern: true while the heartbeat check is in flight, prevents login page flash
  const [isCheckingAuth, setIsCheckingAuth] = useState<boolean>(true);

  // BFF Pattern: Validate authentication by calling the heartbeat endpoint
  // The auth_token HttpOnly cookie is sent automatically by the browser
  const authenticate = async () => {
    setIsCheckingAuth(true);
    try {
      const repository = new AuthRepository();
      await repository.heartbeat(); // 204 = cookie valid
      dispatch(setAutoAuthenticated());
    } catch {
      // Cookie absent or expired — user must log in
      redirectToLogin();
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const redirectIfPasswordChangeRequired = (info: IUserInfoDTO | null) => {
    if (!info?.mustChangePassword) return;
    try {
      if (info?.mustChangePassword && info?.passwordChangeEnabled) {
        const params = new URLSearchParams({
          q: UPDATE_PASSWORD,
        });

        navigate(`${getRoute('login')}?${params}`, { replace: true });
      }
    } catch (error) {
      // Silently handle errors - don't redirect if we can't check
    }
  };

  const handleLoginPreferenceRedirect = async (userPref: IPreferencesDTO) => {
    // If auto-redirect is disabled, redirect the user to Classic; otherwise, keep the user on Bolt.
    // Also skip redirect if user logged in via SSO (isLegacyToBoltSso is true)
    const isLegacyToBoltSso = getLocalStorageValue(LocalStorageEnums.LOGGED_IN_WITH_SSO);
    const isProduction = process.env.NODE_ENV === 'production';
    if (!userPref.essAutoRedirect && !isLegacyToBoltSso && isProduction) {
      const url = await getLegacyUrlAsync();
      if (url) {
        window.location.href = url;
      }
      return;
    }
  };

  const initialize = async (onLoad: (loading: boolean) => void) => {
    onLoad(true);

    try {
      // Getting preferences first to update/get user language preference before calling anything else
      const userPref = await dispatchThunk(fetchPreferences());
      await handleLoginPreferenceRedirect(userPref);

      const userInfo = await dispatchThunk(fetchUserInfo());

      // Intercept and redirect if user must change their password
      redirectIfPasswordChangeRequired(userInfo);

      await Promise.all([
        dispatchThunk(fetchSystemSettings()),
        dispatchThunk(fetchDefaultPreferences()),
        dispatchThunk(fetchLocalizations()),
        dispatchThunk(fetchCultures()),
      ]);
    } catch (e) {
      const { status } = e as IError;

      if (status === 403) {
        toast.info(t('global.oopsYouDontHavePermissionToAccessThisResource'), {
          type: toast.TYPE.ERROR,
        });
        signOut();
      }
    }

    onLoad(false);
  };

  const signOut = async () => {
    try {
      await dispatchThunk(logout());

      redirectToLogin();
    } catch (e) {
      toast.info(t('global.oopsAnErrorOccurredWhileProcessingYourRequest'), { type: toast.TYPE.ERROR });
    } finally {
      // Reset the login SSO flag to prevent auto-redirect on next login
      setIsLegacyToBoltSso(false);
    }
  };

  return { initialize, signOut, authenticate, isCheckingAuth };
};
